// realtime engine で本編を回す画面 (spec 2026-08-29 §6 の段階 1)。
//
// **ChatScreen は触らない。** VoiceStage / ReviewForm / Completion / surveyStore /
// R2 保存だけを共有し、進行の制御だけをここに持つ。共通化は段階 4 (やらない判断もありうる)。
//
// 送信時は cascade の handleSubmit と同じ順序で締める —
// session.ended emit → stopUnloadCapture → finalizeManifest → flush → detach。
// **session.ended は compare.py が完了率と所要時間を取る出所**なので、
// これが出ていないと realtime のセッションはその 2 つが測れない。

import { useCallback, useEffect, useRef, useState } from "react";
import { VoiceStage } from "./VoiceStage";
import { ReviewForm, type ReviewSubmission } from "./ReviewForm";
import { Completion } from "./Completion";
import type { MicState } from "./MicButton";
import { useSurveyStore } from "../state/surveyStore";
import { useChatStore } from "../state/chatStore";
import { loadSurvey } from "../survey/loader";
import { sessionUploader } from "../storage/sessionUploaderInstance";
import { encodeWavPcm16 } from "../audio/wavEncoder";
import { useRealtimeEngine } from "../hooks/useRealtimeEngine";
import { eventBus } from "../observability/eventBus";
import { sessionEventBuffer } from "../storage/sessionEventBufferInstance";
import { makeEvent } from "../observability/eventSchema";
import type {
  AnswerMeta,
  EngineState,
  SpeechEngine,
  UseSpeechEngine,
} from "../conversation/engine";
import type { AnswersArtifact } from "../storage/types";

// cascade の MAX_CONSECUTIVE_FAILURES と同じ値。原則 5 の到達性は engine ではなく
// アプリが持つ (engine が壊れてもテキスト入力に落ちられる)。
const MAX_CONSECUTIVE_REPEATS = 3;

/**
 * engine の状態を既存の MicState へ写す。**"speaking" の向きが逆なので注意** —
 * MicState の "speaking" はユーザーが話している状態で、AI 側は "ai-speaking"。
 */
function toMicState(s: EngineState): MicState {
  switch (s) {
    case "user-speaking":
      return "speaking";
    case "ai-speaking":
      return "ai-speaking";
    case "thinking":
      return "thinking";
    case "listening":
      return "listening";
    default:
      return "idle";
  }
}

type Props = {
  surveyUrl: string;
  userId: string;
  /**
   * 会話エンジン (既定は 2.1)。**画面は 2.1 と live で共有する** (spec 2026-09-14 L2) —
   * 違うのはエンジンだけで、進行・聞き直しの上限・無音の段・R2 保存・計測は 1 組しか持たない。
   * **mount の間は同じ hook を渡すこと** (hook の呼び出し順が変わる)
   */
  useEngine?: UseSpeechEngine;
};

export function RealtimeChatScreen({ surveyUrl, userId, useEngine = useRealtimeEngine }: Props) {
  const [idx, setIdx] = useState(0);
  const [reviewing, setReviewing] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [forceText, setForceText] = useState(false);
  const repeatsRef = useRef<{ qid: string; n: number }>({ qid: "", n: 0 });
  const idxRef = useRef(0);
  const survey = useSurveyStore((s) => s.survey);
  const answers = useSurveyStore((s) => s.answers);
  const setSurvey = useSurveyStore((s) => s.setSurvey);
  const setAnswer = useSurveyStore((s) => s.setAnswer);
  const surveyRef = useRef(survey);
  surveyRef.current = survey;
  const engineRef = useRef<SpeechEngine | null>(null);

  /** 確定回答を R2 へ (spec 2026-08-02 D2)。訂正履歴は realtime にはまだ無い。 */
  const saveAnswers = useCallback(
    (feedback?: string) => {
      try {
        const { sessionId } = useChatStore.getState();
        const { survey: s, answers: current } = useSurveyStore.getState();
        if (!sessionId || !s) return;
        const now = new Date().toISOString();
        const artifact: AnswersArtifact = {
          schemaVersion: 1,
          sessionId,
          userId,
          surveyId: s.id,
          generatedAt: now,
          // 空文字はキーごと省略する (「書かなかった」と「空欄のまま送った」を区別する)
          ...(feedback ? { feedback: { text: feedback, submittedAt: now } } : {}),
          answers: s.questions.map((q) => {
            const a = current[q.id];
            return {
              questionId: q.id,
              // 設問文を焼き込む — survey 定義を変えても過去のセッションがズレない
              questionText: q.text,
              value: a?.value ?? null,
              transcript: a?.transcript ?? null,
              updatedAt: a?.updatedAt ?? 0,
            };
          }),
        };
        sessionUploader.enqueueAnswers({ sessionId, artifact });
      } catch {
        // 保存の失敗で画面を止めない (原則 5)
      }
    },
    [userId]
  );

  /**
   * tool が飛んだかを R2 に残す (spec D16)。**引数の中身は載せない** —
   * 回答本文の正は answers.json 側で、events.jsonl は 48KB の beacon 上限を持つ
   * (answer.corrected と同じ扱い)。
   */
  const emitTool = useCallback(
    (
      name: string,
      // duplicate / stale は live の二重の網で捨てた tool (spec 2026-09-14 §3.5)
      outcome: "ok" | "invalid" | "duplicate" | "stale",
      extra?: Record<string, unknown>
    ) => {
      const { sessionId } = useChatStore.getState();
      if (!sessionId) return;
      eventBus.emit(
        makeEvent("realtime.tool_call", sessionId, {
          engine: engineRef.current?.kind ?? "realtime",
          name,
          outcome,
          ...(extra ?? {}),
        })
      );
    },
    []
  );

  /**
   * AI が読み上げた文を残す (spec D10)。cascade の tts.spoken は文言を載せないが、
   * realtime は毎回生成なので ttsPhrases.ts から復元できない。
   * **設問文が改変された回を後から数える**のが目的なので、そのとき提示していた
   * 設問 id を添える (突き合わせの相手が要る)。
   */
  const handleNarrated = useCallback((text: string) => {
    const { sessionId } = useChatStore.getState();
    if (!sessionId) return;
    const q = surveyRef.current?.questions[idxRef.current];
    eventBus.emit(
      makeEvent("tts.spoken", sessionId, {
        engine: engineRef.current?.kind ?? "realtime",
        text,
        chars: text.length,
        outcome: "played",
        ...(q ? { questionId: q.id } : {}),
      })
    );
  }, []);

  /** 1 応答ぶんの usage。**見積もり (53 円/セッション) を実測に置き換える一次データ。** */
  const handleUsage = useCallback((u: Record<string, unknown>) => {
    const { sessionId } = useChatStore.getState();
    if (!sessionId) return;
    // live は秒課金で形がまったく違う (経過秒・報告秒・backend のトークン)。そのまま残す
    if (engineRef.current?.kind === "live") {
      eventBus.emit(makeEvent("live.usage", sessionId, u));
      return;
    }
    const i = (u.input_token_details ?? {}) as Record<string, number>;
    const o = (u.output_token_details ?? {}) as Record<string, number>;
    eventBus.emit(
      makeEvent("realtime.usage", sessionId, {
        totalTokens: typeof u.total_tokens === "number" ? u.total_tokens : 0,
        audioIn: i.audio_tokens ?? 0,
        textIn: i.text_tokens ?? 0,
        cachedIn: i.cached_tokens ?? 0,
        audioOut: o.audio_tokens ?? 0,
        textOut: o.text_tokens ?? 0,
      })
    );
  }, []);

  const askAt = useCallback((i: number) => {
    const s = surveyRef.current;
    if (!s || i >= s.questions.length) return;
    idxRef.current = i;
    setIdx(i);
    engineRef.current?.setQuestion(s.questions[i]);
  }, []);

  const handleAnswer = useCallback(
    (qid: string, value: string, meta?: AnswerMeta) => {
      emitTool("record_answer", "ok", meta?.via ? { via: meta.via } : undefined);
      // live は窓の中で聞いた入力の文字起こしを持つ (spec 2026-09-14 L13)。value (backend の記録) と
      // transcript の差が「backend が書き換えたか」の一次データになる。2.1 は持たないので value を入れる
      const heard = meta?.heard?.trim();
      setAnswer(qid, value, heard || value);
      if (heard) {
        const { sessionId } = useChatStore.getState();
        if (sessionId) {
          eventBus.emit(
            makeEvent("scribe.final", sessionId, {
              text: heard,
              provider: "gpt-live",
              questionId: qid,
            })
          );
        }
      }
      repeatsRef.current = { qid: "", n: 0 };
      saveAnswers();
      const s = surveyRef.current;
      const next = idxRef.current + 1;
      if (!s || next >= s.questions.length) {
        engineRef.current?.stop();
        setReviewing(true);
        return;
      }
      askAt(next);
    },
    [askAt, emitTool, saveAnswers, setAnswer]
  );

  // reason は段階 3 で R2 のログに載せる。いまは回数だけ数える。
  const handleNeedRepeat = useCallback(
    (qid: string, reason: string) => {
      emitTool("need_repeat", "ok", { reason });
      const r = repeatsRef.current;
      const n = r.qid === qid ? r.n + 1 : 1;
      repeatsRef.current = { qid, n };
      // 声だけで詰まさない。上限に達したらテキスト入力を開く (原則 5)。
      if (n >= MAX_CONSECUTIVE_REPEATS) setForceText(true);
    },
    [emitTool]
  );

  const handleUtteranceAudio = useCallback((audio: Float32Array) => {
    const { sessionId, turnIndex } = useChatStore.getState();
    if (!sessionId) return;
    // fire-and-forget。保存の失敗で回答を止めない (原則 5)。
    try {
      sessionUploader.enqueueAudio({
        sessionId,
        turnIndex,
        turnId: crypto.randomUUID(),
        blob: encodeWavPcm16(audio),
      });
    } catch {
      // 同上
    }
  }, []);

  /**
   * 黙ったまま時間が過ぎた。**realtime には無音の出口が構造的に無い** —
   * turn_detection.create_response は「ユーザーが話し終えた後」にしか効かないので、
   * 黙っていると永久に待つ (2026-09-01 の実機で 61 秒の沈黙を実測)。
   * cascade と同じ段数で、まず声で促し、続けて黙るならテキスト入力へ倒す (原則 5)。
   */
  const handleSilence = useCallback((level: number) => {
    if (level === 0) engineRef.current?.promptSilence();
    else setForceText(true);
  }, []);

  const engine = useEngine({
    onAnswerCommitted: handleAnswer,
    onToolIgnored: (name, reason) => emitTool(name, reason),
    onSilence: handleSilence,
    onNeedRepeat: handleNeedRepeat,
    onError: (e) => {
      // 「上流が壊れた」と「こちらが弾いた」を区別できるようにする
      if (e.code === "tool-invalid") emitTool("record_answer", "invalid");
      setForceText(true);
    },
    onUtteranceAudio: handleUtteranceAudio,
    onNarrated: handleNarrated,
    onUsage: handleUsage,
  });
  engineRef.current = engine;

  useEffect(() => {
    let alive = true;
    void loadSurvey(surveyUrl)
      .then((s) => {
        if (!alive) return;
        setSurvey(s);
        surveyRef.current = s;
        // どちらのエンジンで回したかを R2 から判別する一次キー (spec D16)。
        // ?stt=openai と同じ流儀で **realtime 側だけ** engine を出す (欠落 = cascade)
        // ので、cascade の emit は 1 バイトも変えていない。
        const { sessionId } = useChatStore.getState();
        if (sessionId) {
          eventBus.emit(
            makeEvent("session.started", sessionId, {
              userId,
              surveyId: s.id,
              startedAt: new Date().toISOString(),
              userAgent: navigator.userAgent,
              viewport: { w: window.innerWidth, h: window.innerHeight },
              micGranted: true,
              // engine と、使ったモデル (2.1 は realtimeModel、live は liveModel / liveBackend)
              ...(engineRef.current?.sessionMeta ?? {}),
            })
          );
        }
        // live はセッションの作成時に backend へ設問の一覧を渡す (2.1 は受け取っても使わない)
        return engineRef.current?.start({ purpose: "survey", questions: s.questions });
      })
      .then(() => {
        if (alive) askAt(0);
      })
      .catch(() => {
        // 設問を読めなければ声では進めない。テキスト入力へ倒す (原則 5)。
        if (alive) setForceText(true);
      });
    return () => {
      alive = false;
      engineRef.current?.stop();
    };
    // mount 時に 1 度だけ。engine は毎レンダー新しい object になるので依存に入れない
    // (入れると start/stop を繰り返して WS を張り直し続ける)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyUrl, userId]);

  const startedAtRef = useRef<number>(Date.now());

  const handleSubmit = useCallback(
    (sub: ReviewSubmission) => {
      for (const [qid, v] of Object.entries(sub.values)) setAnswer(qid, v, null);
      saveAnswers(sub.feedback);

      // **store から読み直す** — 確認フォームの送信は setAnswer の直後に同期で
      // ここへ来るので、分割代入した answers は 1 つ前のまま (cascade と同じ理由)。
      const { survey: sv, answers: current } = useSurveyStore.getState();
      const turns = sv?.questions.length ?? 0;
      const answered = Object.values(current).filter(
        (a) => a.value !== null && a.value !== ""
      ).length;
      const { sessionId } = useChatStore.getState();
      if (sessionId) {
        const endedAt = new Date().toISOString();
        eventBus.emit(
          makeEvent("session.ended", sessionId, {
            endedAt,
            status: "completed",
            reason: "submit",
            engine: engineRef.current?.kind ?? "realtime",
            turns,
            answered,
            skipped: Math.max(turns - answered, 0),
            durationMs: Math.max(0, Date.now() - startedAtRef.current),
          })
        );
        // re-arm 由来の abandoned beacon が完了 submit と競合しないよう、
        // await より前に同期で seal する (cascade と同じ、spec 2026-07-25 §3.3)。
        sessionEventBuffer.stopUnloadCapture();
        void (async () => {
          await sessionUploader.finalizeManifest({ sessionId, status: "completed", endedAt });
          sessionEventBuffer.flush("submit");
          sessionEventBuffer.detach();
        })();
      }
      engineRef.current?.stop();
      setSubmitted(true);
    },
    [saveAnswers, setAnswer]
  );

  if (submitted) {
    const total = survey?.questions.length ?? 0;
    const answered = Object.values(answers).filter(
      (a) => a.value !== null && a.value !== ""
    ).length;
    return <Completion answered={answered} total={total} />;
  }

  if (reviewing && survey) {
    return <ReviewForm survey={survey} answers={answers} onSubmit={handleSubmit} />;
  }

  return (
    <VoiceStage
      micState={toMicState(engine.state)}
      onStartRecord={() => {}}
      onSubmitText={(text) => {
        const q = survey?.questions[idx];
        if (q) handleAnswer(q.id, text);
      }}
      forceTextFallback={forceText}
    />
  );
}
